import { ClientLocalization, HubFeature, LocType, PortalType } from '@ats/api/cm';
import { CmCulture } from '@ats/cm/core';
import { LocDisplayType } from './display-type';

/** @deprecated Use only CP or SP instead */
type LocPortalAll = 'All';
export type LocPortal = LocPortalAll | 'SP' | 'CP';

export interface LocalToClientMapConfig {
	name: string;
	localDefinition: LocDef | Localization;
	client?: ClientLocalization;
	currentPortal?: PortalType;
}

export interface LocDef {
	defaultValue: string;
	description: string;
	displayPath: string;
	feature?: HubFeature;
	displayType: LocDisplayType;
	htmlAllowed?: boolean;
	portal: LocPortal;

	/** @deprecated - Use portal instead  */
	type?: LocType;
	/** @default false */
	shared?: boolean;
	legacyName?: string;
}

export interface LocalizationVM
	extends Omit<LocDef, 'htmlAllowed' | 'type' | 'feature'>,
		Required<Pick<LocDef, 'htmlAllowed' | 'feature'>>,
		Pick<ClientLocalization, 'name'> {
	clientValue: string | null;
	value: string | null;
}

export interface LocalizationDialogData {
	loc: LocalizationVM;
	language: string;
	supportedCultures: CmCulture[];
}

export const populateClientLocData = (
	locDef: LocDef & { name: string },
	clientLocOrName: ClientLocalization | string
): LocalizationVM => {
	if (!clientLocOrName) {
		throw new Error('Invalid localization data');
	}
	let name: string;
	let value: string | null = null;
	let clientValue: string | null = null;

	if (typeof clientLocOrName === 'string') {
		name = clientLocOrName;
	} else {
		clientValue = clientLocOrName.value || null;
		name = clientLocOrName.name;
		if (!!clientLocOrName?.value || clientLocOrName?.value === '') {
			value = clientLocOrName?.value;
		} else if (clientLocOrName?.englishValueResolved) {
			value = clientLocOrName.englishValueResolved;
		}
	}

	return {
		...locDef,
		name,
		clientValue,
		htmlAllowed: locDef.htmlAllowed ?? false,
		feature:
			locDef.feature ??
			(locDef.portal === 'CP' ? HubFeature.CandidateAccess : HubFeature.Core),
		value: value ?? (locDef.defaultValue || ''),
	};
};

export type LocDefs<T extends string = string> = Record<T, LocDef>;

export type Localization = Required<Omit<LocDef, 'legacyName'>> &
	Pick<ClientLocalization, 'cultureInfoName' | 'englishValueResolved' | 'value'> & {
		name: string;
		hasClientValue?: boolean;
		isLegacy?: boolean;
	};

export const mapClientLocs = (
	[locs, defs]: [locs: ClientLocalization[], defs: Record<string, LocDef>],
	portal: PortalType
): Record<string, Localization> => {
	return Object.entries(defs).reduce((newLocs, [name, localDefinition]) => {
		const type = getLocType(portal, localDefinition.portal, localDefinition.type);
		const client = locs.find(cloc => cloc.name === name && cloc.locType === type);

		newLocs[name] = mapClientLoc({
			name,
			localDefinition,
			currentPortal: portal,
			client,
		});

		return newLocs;
	}, {} as Record<string, Localization>);
};

export const mapClientLoc = ({
	name,
	localDefinition,
	client,
	currentPortal = PortalType.SponsorPortal,
}: LocalToClientMapConfig): Localization => {
	const locType = client?.locType ? client?.locType : localDefinition.type;
	const type = getLocType(currentPortal, localDefinition.portal, locType);
	const hasClientValue =
		!!((client || {}).value || (client || {}).value === '') ||
		!!(client || {}).englishValueResolved;

	return {
		...localDefinition,
		name,
		type,
		value: getClientValue(client, localDefinition.defaultValue),
		feature:
			localDefinition.feature ||
			(currentPortal === PortalType.SponsorPortal
				? HubFeature.Core
				: HubFeature.CandidateAccess),
		htmlAllowed: localDefinition.htmlAllowed || false,
		shared: localDefinition.shared || false,
		displayType: localDefinition.displayType || 'Content',
		cultureInfoName: (client || {}).cultureInfoName || 'en',
		defaultValue: localDefinition.defaultValue,
		hasClientValue,
		portal:
			localDefinition.portal ||
			((type.toString().includes('CandidatePortal') ? 'CP' : 'SP') as LocPortal),
	};
};

export const getLocTypeFromPortal = (portal: LocPortal): LocType => {
	return portal === 'CP' ? LocType.CandidatePortalString : LocType.SponsorPortalString;
};

const getLocType = (portal: PortalType, locPortal?: LocPortal, type?: LocType): LocType => {
	const currentPortalType =
		portal === PortalType.SponsorPortal
			? LocType.SponsorPortalString
			: LocType.CandidatePortalString;

	// Check if the loc definition has a portal defined on it first. This ALWAYS wins.
	if (locPortal) {
		if (locPortal === 'All') {
			return currentPortalType;
		}
		return locPortal === 'SP' ? LocType.SponsorPortalString : LocType.CandidatePortalString;
	}
	// Check if the loc definition has the older type property next.
	if (type) {
		return type;
	}

	// Default to the current portal
	return currentPortalType;
};

const getClientValue = (client: ClientLocalization | undefined, defaultValue: string): string => {
	if (!client || client.value === '') {
		return '';
	} else {
		return (client || {}).value || (client || {}).englishValueResolved || defaultValue || '';
	}
};

// This is needed now because the type property got removed and we have other type of legacy type localizations in our DB.
// This will no longer be needed one all navigation localizations are moved to the Ichabod.
export const getLocType2 = (loc: LocalizationVM): LocType => {
	let locType = loc.portal === 'CP' ? LocType.CandidatePortalString : LocType.SponsorPortalString;

	if (loc.name.startsWith('/')) {
		locType =
			loc.portal === 'CP' ? LocType.CandidatePortalSystemUrl : LocType.SponsorPortalSystemUrl;
	}

	if (loc.name === 'CiscoCustomReports/_View.Navigation.MenuItem.UserGuide') {
		locType = LocType.SponsorPortalExternalUrl;
	}

	return locType;
};
